import {
  OrganizationProfile,
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  UserButton,
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/clerk-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError, createApi, type Me } from "./api";
import { BrandCard } from "./components/BrandCard";
import { BusinessProfileCard } from "./components/BusinessProfileCard";
import { ClosedLoopCard } from "./components/ClosedLoop";
import { ContentView } from "./components/ContentView";
import { ConversationsCard } from "./components/Conversations";
import { DomainCard } from "./components/DomainCard";
import { LeadAnalyticsCard } from "./components/LeadAnalytics";
import { Leads } from "./components/Leads";
import { PushToggle } from "./components/PushToggle";
import { RedirectsCard } from "./components/RedirectsCard";
import { StaffPanel } from "./components/StaffPanel";
import { WebsiteView } from "./components/WebsiteView";

const BRAND = "Groundbeam";

/** Keeps the embedded Clerk widgets on the Groundline palette. */
const CLERK_APPEARANCE = {
  elements: { rootBox: "clerk-root", card: "clerk-card" },
  variables: {
    borderRadius: "10px",
    colorPrimary: "#2c5c45",
    colorText: "#1b231e",
    colorTextSecondary: "#5b675e",
    fontFamily:
      '"Instrument Sans Variable", "Instrument Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
} as const;
const WORKSPACE_ACTIVATION_RETRY_MS = 300;
const WORKSPACE_ACTIVATION_MAX_ATTEMPTS = 20;

type DashboardErrorState = {
  action: "retry" | "sign-in";
  actionLabel: string;
  message: string;
  testId: "dashboard-load-error" | "auth-session-error";
};

export function dashboardErrorStateFromStatus(
  status: number | null,
  message: string,
): DashboardErrorState {
  if (status === 401) {
    return {
      action: "sign-in",
      actionLabel: "Sign in again",
      message:
        "Groundbeam could not verify your sign-in session. Sign in again to continue.",
      testId: "auth-session-error",
    };
  }
  return {
    action: "retry",
    actionLabel: "Retry",
    message,
    testId: "dashboard-load-error",
  };
}

export function isClerkClientInitError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /setActive is being called before the client is loaded|Wait for init/i.test(
      error.message,
    )
  );
}

export type WorkspaceOption = { id: string; name: string; isCurrent: boolean };

/**
 * Orders a user's workspace memberships for the switcher: the active workspace
 * first, then the rest alphabetically. Kept pure so it can be unit-tested
 * without mounting Clerk.
 */
export function workspaceOptions(
  memberships: { organization: { id: string; name: string } }[],
  activeOrgId: string | null,
): WorkspaceOption[] {
  return memberships
    .map((membership) => ({
      id: membership.organization.id,
      name: membership.organization.name,
      isCurrent: membership.organization.id === activeOrgId,
    }))
    .sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

export type AppView = "dashboard" | "inbox";

export function App({ view = "dashboard" }: { view?: AppView } = {}) {
  return (
    <>
      <SignedOut treatPendingAsSignedOut={false}>
        <Landing />
      </SignedOut>
      <SignedIn treatPendingAsSignedOut={false}>
        <OrgGate view={view} />
      </SignedIn>
    </>
  );
}

function Landing() {
  const isSignUp =
    new URLSearchParams(window.location.search).get("auth") === "sign-up";

  return (
    <div className="auth-shell">
      <div className="auth-hero">
        <Logo />
        <h1>Bring your website into the fast lane.</h1>
        <p>
          Sign in to mirror your existing site onto Groundbeam -
          pixel-for-pixel, static-fast, with your leads flowing into one inbox.
        </p>
      </div>
      <div className="auth-card">
        {isSignUp ? (
          <SignUp
            fallbackRedirectUrl="/"
            forceRedirectUrl="/"
            routing="hash"
            signInUrl="/"
            appearance={CLERK_APPEARANCE}
          />
        ) : (
          <SignIn
            fallbackRedirectUrl="/"
            forceRedirectUrl="/"
            routing="hash"
            signUpUrl="/?auth=sign-up"
            appearance={CLERK_APPEARANCE}
          />
        )}
      </div>
    </div>
  );
}

/** One Clerk Organization == one tenant, but the product exposes workspaces. */
function OrgGate({ view }: { view: AppView }) {
  const { orgId, isLoaded, isSignedIn } = useAuth({
    treatPendingAsSignedOut: false,
  });
  if (!isLoaded) return <FullPageSpinner />;
  if (!isSignedIn) return <Landing />;
  if (!orgId) return <WorkspaceSetup />;
  // Key on orgId so switching workspaces fully remounts the dashboard and
  // reloads /v1/me, pages, and leads for the newly active tenant.
  return <Dashboard key={orgId} view={view} />;
}

function WorkspaceSetup() {
  const { isLoaded: isUserLoaded, user } = useUser();
  const { createOrganization, isLoaded, setActive, userMemberships } =
    useOrganizationList({
      userMemberships: true,
    });
  const suggestedName =
    user?.fullName || user?.primaryEmailAddress?.emailAddress?.split("@")[0];
  const [name, setName] = useState(
    suggestedName ? `${suggestedName}'s workspace` : "",
  );
  const [status, setStatus] = useState<"idle" | "working">("idle");
  const [error, setError] = useState<string | null>(null);

  const memberships = userMemberships.data ?? [];
  const onlyMembershipId =
    memberships.length === 1 ? (memberships[0]?.organization.id ?? null) : null;
  const isBusy = status === "working" || userMemberships.isLoading;

  useEffect(() => {
    if (!isLoaded || !isUserLoaded || !setActive || userMemberships.isLoading) {
      return;
    }
    if (!onlyMembershipId) return;
    let cancelled = false;
    setStatus("working");

    const activateOnlyWorkspace = async () => {
      for (
        let attempt = 0;
        attempt < WORKSPACE_ACTIVATION_MAX_ATTEMPTS;
        attempt += 1
      ) {
        try {
          await setActive({ organization: onlyMembershipId });
          return;
        } catch (error) {
          if (!isClerkClientInitError(error)) break;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, WORKSPACE_ACTIVATION_RETRY_MS),
        );
        if (cancelled) return;
      }
      if (cancelled) return;
      setStatus("idle");
      setError("Could not open your workspace. Please try again.");
    };

    void activateOnlyWorkspace();
    return () => {
      cancelled = true;
    };
  }, [
    isLoaded,
    isUserLoaded,
    onlyMembershipId,
    setActive,
    userMemberships.isLoading,
  ]);

  useEffect(() => {
    if (name || !suggestedName) return;
    setName(`${suggestedName}'s workspace`);
  }, [name, suggestedName]);

  const createWorkspace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!createOrganization || !setActive) return;

    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a workspace name.");
      return;
    }

    setStatus("working");
    setError(null);
    try {
      const organization = await createOrganization({ name: trimmed });
      await setActive({ organization: organization.id });
    } catch {
      setStatus("idle");
      setError("Could not create your workspace. Please try again.");
    }
  };

  const activateWorkspace = async (organizationId: string) => {
    if (!setActive) return;
    setStatus("working");
    setError(null);
    try {
      await setActive({ organization: organizationId });
    } catch {
      setStatus("idle");
      setError("Could not open that workspace. Please try again.");
    }
  };

  if (
    !isLoaded ||
    !isUserLoaded ||
    userMemberships.isLoading ||
    status === "working"
  ) {
    return (
      <WorkspaceShell
        title="Opening your workspace."
        copy="Groundbeam is connecting your account to the right site and lead inbox."
      >
        <FullPageSpinner />
      </WorkspaceShell>
    );
  }

  if (memberships.length > 1) {
    return (
      <WorkspaceShell
        title="Choose your workspace."
        copy="Pick the business you want to work on. You can switch again later."
      >
        <div className="workspace-card">
          <div className="workspace-list">
            {memberships.map((membership) => (
              <button
                className="workspace-option"
                key={membership.organization.id}
                onClick={() => {
                  void activateWorkspace(membership.organization.id);
                }}
                type="button"
              >
                <span>{membership.organization.name}</span>
                <span>Open</span>
              </button>
            ))}
          </div>
          {error && <p className="error">{error}</p>}
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell
      title="Set up your workspace."
      copy="Name the business you want to bring into Groundbeam. Your site, leads, and billing will live here."
    >
      <form
        className="workspace-card workspace-form"
        onSubmit={createWorkspace}
      >
        <label htmlFor="workspace-name">Workspace name</label>
        <input
          autoComplete="organization"
          id="workspace-name"
          onChange={(event) => setName(event.target.value)}
          placeholder="Acme Studio"
          type="text"
          value={name}
        />
        <button disabled={isBusy} type="submit">
          Create workspace
        </button>
        {error && <p className="error">{error}</p>}
      </form>
    </WorkspaceShell>
  );
}

function WorkspaceShell({
  children,
  copy,
  title,
}: {
  children: ReactNode;
  copy: string;
  title: string;
}) {
  return (
    <div className="auth-shell">
      <div className="auth-hero">
        <Logo />
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      <div className="auth-card">{children}</div>
    </div>
  );
}

/**
 * Workspace picker + creator in the dashboard topbar. One Clerk Organization ==
 * one tenant/site, so switching or creating a workspace switches the whole
 * dashboard (the parent keys <Dashboard> on orgId, and the server
 * auto-provisions a fresh tenant + site on the first request under a new org).
 */
function WorkspaceSwitcher() {
  const { organization } = useOrganization();
  const { isLoaded, createOrganization, setActive, userMemberships } =
    useOrganizationList({ userMemberships: true });
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"list" | "create">("list");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"idle" | "working">("idle");
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const busy = status === "working";
  const options = workspaceOptions(
    userMemberships.data ?? [],
    organization?.id ?? null,
  );
  const currentName = organization?.name ?? "Your workspace";

  const close = useCallback(() => {
    setOpen(false);
    setMode("list");
    setName("");
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (open && mode === "create") nameInputRef.current?.focus();
  }, [open, mode]);

  const switchTo = async (organizationId: string) => {
    if (!setActive || busy) return;
    if (organizationId === organization?.id) {
      close();
      return;
    }
    setStatus("working");
    setError(null);
    try {
      // <Dashboard> remounts on the orgId change, so no manual close needed.
      await setActive({ organization: organizationId });
    } catch {
      setStatus("idle");
      setError("Could not open that workspace. Please try again.");
    }
  };

  const createWorkspace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!createOrganization || !setActive || busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a workspace name.");
      return;
    }
    setStatus("working");
    setError(null);
    try {
      const created = await createOrganization({ name: trimmed });
      await setActive({ organization: created.id });
    } catch {
      setStatus("idle");
      setError("Could not create your workspace. Please try again.");
    }
  };

  if (!isLoaded) return null;

  return (
    <div className="workspace-switcher">
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className="workspace-switcher-trigger"
        data-testid="workspace-switcher-trigger"
        onClick={() => (open ? close() : setOpen(true))}
        type="button"
      >
        <span className="workspace-switcher-name">{currentName}</span>
        <span aria-hidden="true" className="workspace-switcher-caret" />
      </button>

      {open && (
        <>
          <div
            aria-hidden="true"
            className="workspace-switcher-scrim"
            onClick={close}
          />
          <div className="workspace-switcher-menu" role="menu">
            {mode === "list" ? (
              <>
                <p className="workspace-switcher-label">Workspaces</p>
                <div className="workspace-switcher-list">
                  {options.map((option) => (
                    <button
                      aria-checked={option.isCurrent}
                      className={
                        option.isCurrent
                          ? "workspace-switcher-item current"
                          : "workspace-switcher-item"
                      }
                      disabled={busy}
                      key={option.id}
                      onClick={() => void switchTo(option.id)}
                      role="menuitemradio"
                      type="button"
                    >
                      <span>{option.name}</span>
                      {option.isCurrent && (
                        <span
                          aria-hidden="true"
                          className="workspace-switcher-check"
                        />
                      )}
                    </button>
                  ))}
                </div>
                <button
                  className="workspace-switcher-new"
                  disabled={busy}
                  onClick={() => {
                    setMode("create");
                    setError(null);
                  }}
                  type="button"
                >
                  + New workspace
                </button>
              </>
            ) : (
              <form
                className="workspace-switcher-create"
                onSubmit={createWorkspace}
              >
                <label htmlFor="new-workspace-name">Workspace name</label>
                <input
                  id="new-workspace-name"
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Acme Studio"
                  ref={nameInputRef}
                  type="text"
                  value={name}
                />
                <div className="workspace-switcher-create-actions">
                  <button
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => {
                      setMode("list");
                      setName("");
                      setError(null);
                    }}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button disabled={busy} type="submit">
                    {busy ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            )}
            {error && <p className="workspace-switcher-error">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
}

export type WorkspaceView =
  | "website"
  | "content"
  | "conversations"
  | "leads"
  | "settings"
  | "staff";

const NAV_ITEMS: {
  view: WorkspaceView;
  label: string;
  path: string;
  adminOnly: boolean;
  /** Platform operators only (me.staff) — the founder back-office. */
  staffOnly?: boolean;
}[] = [
  { view: "website", label: "Website", path: "/", adminOnly: true },
  { view: "content", label: "Content", path: "/content", adminOnly: true },
  {
    view: "conversations",
    label: "Conversations",
    path: "/conversations",
    adminOnly: false,
  },
  { view: "leads", label: "Leads", path: "/leads", adminOnly: false },
  { view: "settings", label: "Settings", path: "/settings", adminOnly: true },
  {
    view: "staff",
    label: "Staff",
    path: "/staff",
    // Gated by me.staff alone (matching the API): a platform operator keeps
    // the back-office even if their role in the current org is agent.
    adminOnly: false,
    staffOnly: true,
  },
];

function viewFromPath(pathname: string): WorkspaceView {
  const match = NAV_ITEMS.find(
    (item) => item.path !== "/" && pathname.startsWith(item.path),
  );
  return match?.view ?? "website";
}

/**
 * Staff is the only view whose availability depends on loaded data
 * (/v1/me.staff): a non-staff deep link to /staff falls back to Website
 * instead of rendering an empty pane.
 */
export function effectiveWorkspaceView(
  view: WorkspaceView,
  isStaff: boolean,
): WorkspaceView {
  return view === "staff" && !isStaff ? "website" : view;
}

function Dashboard({ view }: { view: AppView }) {
  const inboxMode = view === "inbox";
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>(() =>
    viewFromPath(window.location.pathname),
  );
  const navigate = useCallback((next: WorkspaceView) => {
    const item = NAV_ITEMS.find((i) => i.view === next);
    window.history.pushState(null, "", item?.path ?? "/");
    setWorkspaceView(next);
  }, []);
  useEffect(() => {
    const onPop = () =>
      setWorkspaceView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const api = useMemo(() => createApi(getToken), [getToken]);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<DashboardErrorState | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [editorRefreshKey, setEditorRefreshKey] = useState(0);
  const canGenerate = me?.entitlements?.features?.generate_site === true;
  const isAdmin = me?.role !== "agent";
  const activeView = effectiveWorkspaceView(workspaceView, me?.staff === true);
  const [showTeam, setShowTeam] = useState(false);

  // Load /v1/me, retrying briefly while server-side auto-provisioning of a
  // brand-new org settles (the first access can 403 for a beat). Returns a
  // canceller so the effect can abort on unmount.
  const load = useCallback(() => {
    let cancelled = false;
    let attempts = 0;

    const attempt = async () => {
      attempts += 1;
      setLoading(true);
      try {
        const next = await api.me();
        if (cancelled) return;
        setMe(next);
        setError(null);
        setSettingUp(false);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // Server-driven setup: each poll advances provisioning where the
        // last run stopped, so keep polling calmly (~5 min ceiling).
        if (err instanceof ApiError && err.provisioning && attempts < 100) {
          setSettingUp(true);
          setError(null);
          setTimeout(attempt, 3000);
          return;
        }
        if (err instanceof ApiError && err.status === 403 && attempts < 6) {
          setSettingUp(true);
          setError(null);
          setTimeout(attempt, 2500);
          return;
        }
        setSettingUp(false);
        setError(
          dashboardErrorStateFromStatus(
            err instanceof ApiError ? err.status : null,
            err instanceof ApiError
              ? err.message
              : "Could not reach the server.",
          ),
        );
        setLoading(false);
      }
    };

    attempt();
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const heartbeat = () => void api.presence().catch(() => undefined);
    heartbeat();
    const interval = window.setInterval(heartbeat, 60_000);
    return () => window.clearInterval(interval);
  }, [api]);

  const refreshEditorAfterImport = useCallback(() => {
    setEditorRefreshKey((current) => current + 1);
  }, []);

  return (
    <div className="app">
      <header className="topbar">
        <Logo />
        <div className="topbar-right">
          <WorkspaceSwitcher />
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>

      <main className={me?.hostname ? "content workspace-ready" : "content"}>
        {settingUp && !me && (
          <div className="setup-panel" data-testid="workspace-setting-up">
            <div className="spinner" aria-hidden="true" />
            <h2>Setting up your workspace</h2>
            <p className="muted">
              Creating your database, storage and starter site. This usually
              takes under a minute — no need to refresh.
            </p>
          </div>
        )}
        {loading && !me && !settingUp && <FullPageSpinner />}
        {error && !me && !settingUp && (
          <div className="notice" data-testid={error.testId}>
            <p>{error.message}</p>
            <button
              type="button"
              onClick={() => {
                if (error.action === "sign-in") {
                  void signOut({ redirectUrl: "/" });
                } else {
                  load();
                }
              }}
            >
              {error.actionLabel}
            </button>
          </div>
        )}
        {me && inboxMode && (
          <div className="inbox-shell">
            <header className="inbox-head">
              <h1>{me.businessName ?? "Inbox"}</h1>
              <div className="inbox-head-actions">
                <PushToggle api={api} />
                <a className="muted small" href="/">
                  Full dashboard
                </a>
              </div>
            </header>
            <ConversationsCard api={api} />
            <Leads api={api} />
          </div>
        )}
        {me && !inboxMode && (
          <div className="workspace-layout">
            <nav className="side-nav" aria-label="Workspace">
              <div className="side-nav-workspace">
                <span className="side-nav-name">
                  {me.businessName ?? "Your workspace"}
                </span>
                <span className="plan-pill">{me.plan}</span>
              </div>
              {NAV_ITEMS.filter(
                (item) =>
                  (isAdmin || !item.adminOnly) &&
                  (!item.staffOnly || me.staff === true),
              ).map((item) => (
                <button
                  key={item.view}
                  type="button"
                  className={activeView === item.view ? "active" : ""}
                  onClick={() => navigate(item.view)}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="view-pane">
              {activeView === "website" &&
                (isAdmin ? (
                  <WebsiteView
                    api={api}
                    me={me}
                    canGenerate={canGenerate}
                    editorRefreshKey={editorRefreshKey}
                    onImportComplete={refreshEditorAfterImport}
                  />
                ) : (
                  <ConversationsCard api={api} />
                ))}
              {activeView === "content" && isAdmin && <ContentView api={api} />}
              {activeView === "conversations" && (
                <ConversationsCard api={api} />
              )}
              {activeView === "leads" && (
                <>
                  <Leads api={api} />
                  <LeadAnalyticsCard api={api} />
                  {isAdmin && <ClosedLoopCard api={api} />}
                </>
              )}
              {activeView === "staff" && me.staff === true && (
                <StaffPanel api={api} />
              )}
              {activeView === "settings" && isAdmin && (
                <>
                  <BrandCard api={api} />
                  <DomainCard
                    api={api}
                    entitled={
                      me.entitlements?.features?.["custom_domain"] === true
                    }
                  />
                  <BusinessProfileCard api={api} />
                  <RedirectsCard api={api} />
                </>
              )}
              {activeView === "settings" && isAdmin && (
                <section className="card team-card">
                  <div className="card-head">
                    <div>
                      <h2>Team</h2>
                      <p className="muted">
                        Invite staff to handle chats and leads. Members see only
                        Conversations and Leads — here and in the mobile inbox.
                      </p>
                    </div>
                    <button
                      className="btn-secondary"
                      onClick={() => setShowTeam((current) => !current)}
                      type="button"
                    >
                      {showTeam ? "Close" : "Manage team"}
                    </button>
                  </div>
                  {showTeam && (
                    <div className="team-profile">
                      <OrganizationProfile routing="virtual" />
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Logo() {
  return (
    <span className="logo">
      <span className="logo-mark" aria-hidden="true" />
      {BRAND}
    </span>
  );
}

function FullPageSpinner() {
  return (
    <div className="spinner-wrap">
      <div className="spinner" />
    </div>
  );
}
